import { normalizeProductionUnit } from "../production/quantity.js";
import { reviewedPackagingWorkOrderMatches } from "./work-order-matches.js";
import { actualQuantity, excelRowPlanQuantity, sharedRowActuals, weekRemainingQuantity, type QuantityResult } from "./packaging-quantities.js";
import { matchesPackagingProductCodeFilters, type PackagingProductFilters } from "./packaging-product-catalog.js";
import { productionLineFromDocument } from "./packaging-production-routing.js";
import { isManualPackagingProductionDocument } from "./production-document-lines.js";
import type { PackagingPlanRow, Week39PackagingLive, Week39PackagingSnapshot } from "./packaging-report-types.js";

export type PackagingCarryoverStatus = "confirmed" | "ambiguous";
export type PackagingCarryoverRow = {
  sourceRow: number;
  pairSourceRows: number[];
  lineName: string;
  workOrderNo: string;
  productCode: string;
  productName: string;
  country: string | null;
  opening: QuantityResult;
  currentProduction: QuantityResult;
  /** Exact current-week movements allocated once to this prior source row. */
  currentActualByDate: Array<{ date: string; quantity: number; productionLine: string | null }> | null;
  remaining: QuantityResult;
  pairExcess: QuantityResult;
  status: PackagingCarryoverStatus;
  candidateCurrentOrders: string[];
};

export type PackagingCarryoverSummary = {
  opening: QuantityResult;
  currentProduction: QuantityResult;
  remaining: QuantityResult;
  excess: QuantityResult;
  rowCount: number;
  knownRowCount: number;
};

export type PackagingCarryover = {
  previousWeekStart: string;
  rows: PackagingCarryoverRow[];
  confirmedRows: PackagingCarryoverRow[];
  ambiguousRows: PackagingCarryoverRow[];
  summary: PackagingCarryoverSummary;
  confirmedSummary: PackagingCarryoverSummary;
  ambiguousSummary: PackagingCarryoverSummary;
};

export type PackagingCarryoverFilters = {
  selectedLine: string;
  search: string;
  productFilters: PackagingProductFilters;
  hideCompleted: boolean;
};

type PriorRow = { plan: PackagingPlanRow; lineName: string; opening: QuantityResult; key: string };
type PairActual = { total: QuantityResult; byDate: Array<{ date: string; quantity: number; productionLine: string | null }> | null };
type PairState = { rows: PriorRow[]; actual: PairActual; status: PackagingCarryoverStatus; candidates: string[] };

// Mikro SELECT review, 2026-10-21: same sales line, distinct production batch IDs.
// 3400/1: batches 67 (600) + 70 (700); IHR3206/0: batches 1 (320) + 4 (1580).
// These are separate output orders, so current-batch output must never settle the old batch.
const reviewedSeparateBatches = [
  { previousWeek: "2026-10-12", currentWeek: "2026-10-19", previousOrder: "003400 067", currentOrder: "003400 070", productCode: "19025990", country: "KAZAKİSTAN" },
  { previousWeek: "2026-10-12", currentWeek: "2026-10-19", previousOrder: "003206 001", currentOrder: "003206 004", productCode: "19000111", country: "ÖZBEKİSTAN" },
  // Mikro SELECT review 2026-10-26, Refs #52: these old/new outputs have
  // different sales orders AND customers. New output cannot settle the old balance.
  { previousWeek: "2026-10-19", currentWeek: "2026-10-26", previousOrder: "003431 013", currentOrder: "003434 001", productCode: "19000576", country: "TÜRKİYE" },
  { previousWeek: "2026-10-19", currentWeek: "2026-10-26", previousOrder: "003554 041", currentOrder: "003570 001", productCode: "19060828", country: "SİNGAPUR" },
  { previousWeek: "2026-10-19", currentWeek: "2026-10-26", previousOrder: "003554 041", currentOrder: "003572 001", productCode: "19060828", country: "SİNGAPUR" },
  { previousWeek: "2026-10-19", currentWeek: "2026-10-26", previousOrder: "003554 051", currentOrder: "003570 006", productCode: "19060833", country: "SİNGAPUR" },
  { previousWeek: "2026-10-19", currentWeek: "2026-10-26", previousOrder: "003554 051", currentOrder: "003572 007", productCode: "19060833", country: "SİNGAPUR" },
];

function separateBatchVerified(previousWeek: string, currentWeek: string, row: PackagingPlanRow, candidate: string) {
  return reviewedSeparateBatches.some((match) => match.previousWeek === previousWeek && match.currentWeek === currentWeek
      && match.previousOrder === row.workOrderNo
      && match.currentOrder === candidate && match.productCode === row.productCode && match.country === row.country);
}

const zero = (): QuantityResult => ({ value: 0, unit: "KOLİ", reason: null });
const unknown = (reason: string): QuantityResult => ({ value: null, unit: null, reason });

function weekEnd(weekStart: string) {
  const date = new Date(`${weekStart}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 6);
  return date.toISOString().slice(0, 10);
}

function normalizeSearch(value: string) {
  return value.toLocaleLowerCase("tr-TR").normalize("NFD").replace(/[\u0300-\u036f]/gu, "").replace(/ı/gu, "i").trim();
}

function knownQuantity(value: QuantityResult): number | null {
  return value.value !== null && Number.isFinite(value.value) && value.unit === "KOLİ" ? value.value : null;
}

function sumKnown(values: QuantityResult[], label: string): QuantityResult {
  const amounts = values.map(knownQuantity);
  if (amounts.some((value) => value === null)) return unknown(label);
  return { value: amounts.reduce<number>((sum, value) => sum + (value ?? 0), 0), unit: "KOLİ", reason: null };
}

function aliasKey(workOrderNo: string, productCode: string) {
  return `${workOrderNo}|${productCode}`;
}

function aliases(snapshot: Week39PackagingSnapshot, live: Week39PackagingLive | null) {
  const map = new Map<string, string>();
  for (const match of [...reviewedPackagingWorkOrderMatches(snapshot), ...(live?.workOrderMatches ?? [])]) {
    map.set(aliasKey(match.excelWorkOrderNo, match.productCode), match.excelWorkOrderNo);
    map.set(aliasKey(match.mikroWorkOrderNo, match.productCode), match.excelWorkOrderNo);
  }
  return map;
}

function pairKey(workOrderNo: string, productCode: string, aliasesByOrder: Map<string, string>) {
  return `${aliasesByOrder.get(aliasKey(workOrderNo, productCode)) ?? workOrderNo}|${productCode}`;
}

function normalizedLive(live: Week39PackagingLive | null, aliasesByOrder: Map<string, string>): Week39PackagingLive | null {
  if (!live) return null;
  return {
    ...live,
    // Work-order state has no product code. Do not apply a product-specific alias to it.
    workOrders: live.workOrders.map((item) => ({ ...item })),
    items: live.items.map((item) => ({ ...item,
      workOrderNo: aliasesByOrder.get(aliasKey(item.workOrderNo, item.productCode))
        ?? aliasesByOrder.get(aliasKey(item.mikroWorkOrderNo ?? "", item.productCode)) ?? item.workOrderNo,
    })),
  };
}

function closingBalance(snapshot: Week39PackagingSnapshot, live: Week39PackagingLive | null, row: PackagingPlanRow, sharedRows: number): QuantityResult {
  if (!live) return unknown("Önceki haftanın Mikro verisi alınamadı");
  if (live.range.from > snapshot.weekStart || live.range.to < weekEnd(snapshot.weekStart)) {
    return unknown("Önceki hafta kapanış günü Mikro sorgusunda yok");
  }
  const plan = excelRowPlanQuantity(row);
  const actual = sharedRows > 1
    ? sharedRowActuals(snapshot, row, weekEnd(snapshot.weekStart), live).cumulative
    : actualQuantity(snapshot, row, weekEnd(snapshot.weekStart), live, "week");
  return weekRemainingQuantity(plan, actual);
}

function currentPairActual(currentSnapshot: Week39PackagingSnapshot, live: Week39PackagingLive | null, key: string,
  aliasesByOrder: Map<string, string>): PairActual {
  if (!live) return { total: unknown("Bu haftanın Mikro verisi alınamadı"), byDate: null };
  if (live.range.from > currentSnapshot.weekStart || live.range.to < currentSnapshot.weekStart) {
    return { total: unknown("Bu hafta başlangıcı Mikro sorgusunda yok"), byDate: null };
  }
  const through = [live.range.to, weekEnd(currentSnapshot.weekStart)].sort()[0];
  const candidates = live.items.filter((item) => item.date >= currentSnapshot.weekStart && item.date <= through
    && pairKey(item.workOrderNo, item.productCode, aliasesByOrder) === key);
  if (candidates.some((item) => productionLineFromDocument(item.documentNo) === null)) {
    return { total: unknown("Bu haftaki Mikro belgesinin hattı doğrulanamadı"), byDate: null };
  }
  const rows = candidates.filter((item) => isManualPackagingProductionDocument(item.documentNo));
  if (rows.some((item) => !Number.isFinite(item.quantity) || !item.unit || normalizeProductionUnit(item.unit) !== "KOLİ")) {
    return { total: unknown("Bu haftaki Mikro hareketlerinin birimi KOLİ olarak doğrulanamadı"), byDate: null };
  }
  const byDate = [...new Map(rows.reduce((values, item) => {
    const productionLine = productionLineFromDocument(item.documentNo);
    const key = `${item.date}|${productionLine ?? ""}`;
    values.set(key, { date: item.date, productionLine, quantity: (values.get(key)?.quantity ?? 0) + item.quantity });
    return values;
  }, new Map<string, { date: string; productionLine: string | null; quantity: number }>()))].map(([, item]) => item)
    .sort((left, right) => left.date.localeCompare(right.date) || (left.productionLine ?? "").localeCompare(right.productionLine ?? "", "tr-TR"));
  const value = byDate.reduce((sum, item) => sum + item.quantity, 0);
  if (value < 0 || byDate.some((item) => item.quantity < 0)) {
    return { total: unknown("Bu haftaki Mikro net üretimi negatif; devreden bakiye otomatik değiştirilemedi"), byDate: null };
  }
  return { total: { value, unit: "KOLİ", reason: null }, byDate };
}

function summarize(rows: PackagingCarryoverRow[]): PackagingCarryoverSummary {
  return {
    opening: sumKnown(rows.map((row) => row.opening), "Devreden açılış toplamı doğrulanamadı"),
    currentProduction: sumKnown(rows.map((row) => row.currentProduction), "Bu hafta devreden üretim toplamı doğrulanamadı"),
    remaining: sumKnown(rows.map((row) => row.remaining), "Devreden kalan toplamı doğrulanamadı"),
    excess: sumKnown(rows.map((row) => row.pairExcess), "Devreden fazla üretim toplamı doğrulanamadı"),
    rowCount: rows.length,
    knownRowCount: rows.filter((row) => row.remaining.value !== null).length,
  };
}

/**
 * Keeps the prior week as a separate, undated backlog. It never changes the current Excel target
 * and excludes a work-order/product pair that is already present in the current plan.
 */
export function buildPackagingCarryover(previousSnapshot: Week39PackagingSnapshot, previousLive: Week39PackagingLive | null,
  currentSnapshot: Week39PackagingSnapshot, currentLive: Week39PackagingLive | null): PackagingCarryover {
  const previousAliases = aliases(previousSnapshot, previousLive);
  const currentAliases = aliases(currentSnapshot, currentLive);
  const allAliases = new Map([...previousAliases, ...currentAliases]);
  const priorLive = normalizedLive(previousLive, allAliases);
  const live = normalizedLive(currentLive, allAliases);
  const currentPairs = new Set(currentSnapshot.lines.flatMap((line) => line.rows)
    .filter((row) => row.workOrderStatus !== "PENDING" && !row.productionOnly && !row.unplanned)
    .map((row) => pairKey(row.workOrderNo, row.productCode, allAliases)));
  const currentByProductCountry = new Map<string, Set<string>>();
  for (const line of currentSnapshot.lines) for (const row of line.rows) {
    if (!row.country?.trim() || row.workOrderStatus === "PENDING" || row.productionOnly || row.unplanned) continue;
    const productCountry = `${row.productCode}|${row.country.trim()}`;
    const orders = currentByProductCountry.get(productCountry) ?? new Set<string>();
    orders.add(row.workOrderNo); currentByProductCountry.set(productCountry, orders);
  }
  const pairCounts = new Map<string, number>();
  for (const line of previousSnapshot.lines) for (const row of line.rows) {
    const key = pairKey(row.workOrderNo, row.productCode, allAliases);
    pairCounts.set(key, (pairCounts.get(key) ?? 0) + 1);
  }
  const states = new Map<string, PairState>();
  for (const line of previousSnapshot.lines) for (const row of line.rows) {
    if (row.workOrderStatus === "PENDING") continue;
    const key = pairKey(row.workOrderNo, row.productCode, allAliases);
    if (currentPairs.has(key)) continue;
    const opening = closingBalance(previousSnapshot, priorLive, row, pairCounts.get(key) ?? 1);
    if (knownQuantity(opening) === 0) continue;
    const candidates = row.country?.trim() ? [...(currentByProductCountry.get(`${row.productCode}|${row.country.trim()}`) ?? [])]
      .filter((workOrderNo) => pairKey(workOrderNo, row.productCode, allAliases) !== key
        && !separateBatchVerified(previousSnapshot.weekStart, currentSnapshot.weekStart, row, workOrderNo)).sort() : [];
    const state = states.get(key) ?? { rows: [], actual: currentPairActual(currentSnapshot, live, key, allAliases),
      status: candidates.length ? "ambiguous" : "confirmed", candidates };
    state.rows.push({ plan: row, lineName: line.name, opening, key });
    states.set(key, state);
  }
  const rows: PackagingCarryoverRow[] = [];
  for (const state of states.values()) {
    const pairSourceRows = state.rows.map((row) => row.plan.sourceRow).sort((a, b) => a - b);
    const openingTotal = sumKnown(state.rows.map((row) => row.opening), "Devreden açılış miktarı doğrulanamadı");
    const actual = knownQuantity(state.actual.total);
    const opening = knownQuantity(openingTotal);
    let available = actual ?? 0;
    const pairExcess = actual === null || opening === null ? unknown(state.actual.total.reason ?? openingTotal.reason ?? "Devreden fazla üretim doğrulanamadı")
      : { value: Math.max(0, actual - opening), unit: "KOLİ", reason: null };
    const dateActuals = new Map<number, Array<{ date: string; quantity: number; productionLine: string | null }>>();
    if (actual !== null && opening !== null && state.actual.byDate !== null) {
      const capacities = new Map(state.rows.map((row) => [row.plan.sourceRow, knownQuantity(row.opening) ?? 0]));
      const firstSourceRow = [...state.rows].sort((left, right) => left.plan.sourceRow - right.plan.sourceRow)[0]?.plan.sourceRow;
      for (const movement of state.actual.byDate) {
        let remaining = movement.quantity;
        for (const prior of [...state.rows].sort((left, right) => left.plan.sourceRow - right.plan.sourceRow)) {
          const capacity = capacities.get(prior.plan.sourceRow) ?? 0;
          const amount = Math.min(capacity, remaining);
          if (amount > 0) {
            const values = dateActuals.get(prior.plan.sourceRow) ?? [];
            values.push({ date: movement.date, quantity: amount, productionLine: movement.productionLine }); dateActuals.set(prior.plan.sourceRow, values);
            capacities.set(prior.plan.sourceRow, capacity - amount); remaining -= amount;
          }
        }
        if (remaining > 0 && firstSourceRow !== undefined) {
          const values = dateActuals.get(firstSourceRow) ?? [];
          values.push({ date: movement.date, quantity: remaining, productionLine: movement.productionLine }); dateActuals.set(firstSourceRow, values);
        }
      }
    }
    for (const prior of [...state.rows].sort((left, right) => left.plan.sourceRow - right.plan.sourceRow)) {
      const amount = knownQuantity(prior.opening);
      const fulfilled = amount === null || opening === null || actual === null ? unknown(state.actual.total.reason ?? openingTotal.reason ?? prior.opening.reason ?? "Devreden üretim doğrulanamadı")
        : { value: Math.min(amount, available), unit: "KOLİ", reason: null };
      if (fulfilled.value !== null) available = Math.max(0, available - fulfilled.value);
      const remaining = amount === null || opening === null || fulfilled.value === null ? unknown(fulfilled.reason ?? openingTotal.reason ?? prior.opening.reason ?? "Devreden kalan doğrulanamadı")
        : { value: Math.max(0, amount - fulfilled.value), unit: "KOLİ", reason: null };
      rows.push({ sourceRow: prior.plan.sourceRow, pairSourceRows, lineName: prior.lineName, workOrderNo: prior.plan.workOrderNo,
        productCode: prior.plan.productCode, productName: prior.plan.productName, country: prior.plan.country, opening: prior.opening,
        currentProduction: fulfilled, currentActualByDate: actual === null || opening === null ? null : dateActuals.get(prior.plan.sourceRow) ?? [],
        remaining, pairExcess: prior.plan.sourceRow === pairSourceRows[0] ? pairExcess : zero(),
        status: state.status, candidateCurrentOrders: state.candidates });
    }
  }
  rows.sort((left, right) => left.lineName.localeCompare(right.lineName, "tr-TR") || left.sourceRow - right.sourceRow);
  const confirmedRows = rows.filter((row) => row.status === "confirmed");
  const ambiguousRows = rows.filter((row) => row.status === "ambiguous");
  return { previousWeekStart: previousSnapshot.weekStart, rows, confirmedRows, ambiguousRows, summary: summarize(rows),
    confirmedSummary: summarize(confirmedRows), ambiguousSummary: summarize(ambiguousRows) };
}

/**
 * Adds verified prior-week closing balances to the selected week's in-memory plan only.
 * These rows intentionally have no daily Excel allocation: their weekly target is traceable
 * to the prior closing balance and their current output is allocated once by exact pair.
 */
export function buildPackagingPlanWithCarryover(snapshot: Week39PackagingSnapshot, carryover: PackagingCarryover | null): Week39PackagingSnapshot {
  if (!carryover || snapshot.carryoverIntegrated) return snapshot;
  type Group = { rows: PackagingCarryoverRow[]; lineName: string; ambiguous: boolean };
  const groups = new Map<string, Group>();
  for (const row of carryover.rows) {
    const key = `${row.lineName}|${row.workOrderNo}|${row.productCode}`;
    const group = groups.get(key) ?? { rows: [], lineName: row.lineName, ambiguous: false };
    group.ambiguous ||= row.status === "ambiguous";
    group.rows.push(row); groups.set(key, group);
  }
  if (!groups.size) return { ...snapshot, carryoverIntegrated: true };
  const lines = new Map(snapshot.lines.map((line) => [line.name, { ...line, rows: [...line.rows] }]));
  let sourceRow = -1_000_000;
  for (const group of [...groups.values()].sort((left, right) => left.lineName.localeCompare(right.lineName, "tr-TR")
    || left.rows[0].workOrderNo.localeCompare(right.rows[0].workOrderNo, "tr-TR") || left.rows[0].productCode.localeCompare(right.rows[0].productCode))) {
    const rows = [...group.rows].sort((left, right) => left.sourceRow - right.sourceRow);
    const first = rows[0];
    const opening = group.ambiguous ? unknown("Devreden işin yeni iş emri eşleşmesi doğrulanamadı")
      : sumKnown(rows.map((row) => row.opening), "Devreden açılış miktarı doğrulanamadı");
    const actualsByDate = rows.some((row) => row.currentActualByDate === null) ? null : [...new Map(rows.flatMap((row) => row.currentActualByDate ?? [])
      .reduce((values, item) => {
        const key = `${item.date}|${item.productionLine ?? ""}`;
        values.set(key, (values.get(key) ?? 0) + item.quantity);
        return values;
      }, new Map<string, number>()))]
      .map(([key, quantity]) => { const [date, productionLine = ""] = key.split("|"); return { date, quantity, productionLine: productionLine || null }; })
      .sort((left, right) => left.date.localeCompare(right.date) || (left.productionLine ?? "").localeCompare(right.productionLine ?? "", "tr-TR"));
    const weeklyActual = actualsByDate === null ? unknown("Devreden işin bu hafta üretimi doğrulanamadı")
      : { value: actualsByDate.reduce((sum, item) => sum + item.quantity, 0), unit: "KOLİ" as const, reason: null };
    const countries = [...new Set(rows.map((row) => row.country?.trim()).filter((value): value is string => Boolean(value)))];
    let line = lines.get(group.lineName);
    if (!line) {
      line = { name: group.lineName, excelName: null, mappingStatus: "mapped", footerCell: null, rows: [] };
      lines.set(group.lineName, line);
    }
    line.rows.push({ sourceRow: sourceRow--, workOrderNo: first.workOrderNo, productCode: first.productCode,
      productName: first.productName, country: countries.length === 1 ? countries[0] : null, note: null, allocations: [],
      carryover: { previousWeekStart: carryover.previousWeekStart, quantity: opening.value, reason: opening.reason,
        weeklyActual: weeklyActual.value, weeklyActualReason: weeklyActual.reason,
        actualsByDate,
        sourceRows: [...new Set(rows.map((row) => row.sourceRow))].sort((left, right) => left - right) } });
  }
  return { ...snapshot, carryoverIntegrated: true, lines: [...lines.values()] };
}

export function selectPackagingCarryover(carryover: PackagingCarryover, filters: PackagingCarryoverFilters) {
  const search = normalizeSearch(filters.search);
  return carryover.rows.filter((row) => {
    if (filters.selectedLine && row.lineName !== filters.selectedLine) return false;
    if (!matchesPackagingProductCodeFilters(row.productCode, filters.productFilters)) return false;
    if (filters.hideCompleted && row.remaining.value === 0) return false;
    const searchable = normalizeSearch(`${row.lineName} ${row.workOrderNo} ${row.workOrderNo.replace(/\s+/gu, "")} ${row.productCode} ${row.productName} ${row.country ?? ""}`);
    return !search || search.split(/\s+/u).every((token) => searchable.includes(token));
  });
}
